import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import {
  approvedOptions,
  toSellOptions,
  propertyTypeOptions,
  validationSchema,
} from '@propertypal/shared/src/constants/registerYourInterest';
import useLocalStorage from '@propertypal/shared/src/hooks/useLocalStorage';
import { newEnquiryRecaptcha } from '@propertypal/shared/src/reducers/enquiries';
import { useAppDispatch } from '@propertypal/shared/src/reducers/store';
import { setItem } from '@propertypal/shared/src/services/local.storage';
import { Property } from '@propertypal/shared/src/types';
import { CustomData } from '@propertypal/shared/src/types/enquiries';
import { WebsiteUser } from '@propertypal/shared/src/types/websiteUser';
import getAccountNumber from '@propertypal/shared/src/utils/property/getAccountNumber';
import getSellingAgent, { getAgentColours } from '@propertypal/shared/src/utils/property/getSellingAgent';
import Button from '@propertypal/web-ui/src/buttons/Button';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import Checkbox from '@propertypal/web-ui/src/inputs/Checkbox';
import PhoneNumberInput from '@propertypal/web-ui/src/inputs/PhoneNumberInput';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import TextArea from '@propertypal/web-ui/src/inputs/TextArea';
import TextInput from '@propertypal/web-ui/src/inputs/TextInput';
import { Box } from '@propertypal/web-ui/src/layout';
import Recaptcha, { getCaptchaKey } from '@propertypal/web-ui/src/security/Recaptcha';
import { openToast } from '@propertypal/web-ui/src/toast/ToastContext';
import { SubHeading, Text } from '@propertypal/web-ui/src/typography';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import useCachedUser, { PROFILE_STORAGE_KEY } from '../../../hooks/useCachedUser';
import {
  EnquiryFormColumn,
  EnquiryFormContainer,
  EnquiryFormInput,
  EnquiryFormButtonContainer,
} from './EnquiryForm.style.pn';

interface EnquiryFormProps {
  onSuccess: (sentName: string, leadId?: number) => void;
  property: Property;
  initialAccountNumber?: string;
  registerInterestModal?: boolean;
  loggedInUser?: WebsiteUser;
  loggedIn: boolean;
}

// 4:3 Ratio, img will be no larger than 340px
const IMAGE_LOCATOR = '340x255:FILL_CROP';

const getDefaultText = (address: string) => `I am interested in ${address}. Please contact me about this property.`;

// eslint-disable-next-line max-len
const regInterestBodyPlaceholder = `For example; when is best to contact you/ general queries about the development and house buying process etc`;

const EnquiryForm: React.FC<EnquiryFormProps> = ({
  property,
  onSuccess,
  initialAccountNumber,
  registerInterestModal,
  loggedInUser,
  loggedIn,
}) => {
  const [regInterestData, setRegInterestData] = useLocalStorage<CustomData | null>('registerInterest', null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const sellingAgent = getSellingAgent(property);
  const { pdpBrandingButtonBgColour, pdpBrandingButtonTextColour } = getAgentColours(
    property.enhancedBranding,
    sellingAgent,
  );
  const asyncDispatch = useAppDispatch();
  const defaultText = getDefaultText(property.displayAddress);
  const accountNumber = initialAccountNumber || getAccountNumber(property);

  const contactOptions =
    property.contacts?.all.map((contact) => ({
      label: contact.organisation,
      value: contact.accountNumber,
    })) || [];

  if (contactOptions && contactOptions.length > 1 && property.contacts?.showAllContactsOption) {
    contactOptions.unshift({
      label: 'Contact all',
      value: 'all',
    });
  }

  const formik = useFormik({
    initialValues: {
      accountNumber,
      email: '',
      prefix: '+44',
      tel: '',
      name: '',
      body: registerInterestModal ? '' : defaultText,
      propertyType: regInterestData?.['Property Type'] || '',
      budgetRange: regInterestData?.['Approximate Budget Range'] || '',
      areYouApproved: regInterestData?.['Are you approved for a mortgage?'] || '',
      propertyToSell: regInterestData?.['Do you have a property to sell?'] || '',
      rememberMe: false,
      requestViewing: false,
    },
    validationSchema: validationSchema(registerInterestModal),
    onSubmit: async (values, helpers) => {
      if (values.rememberMe) {
        setItem(PROFILE_STORAGE_KEY, {
          name: values.name,
          email: values.email,
          phoneNumber: `${values.prefix}${values.tel}`,
        });

        if (registerInterestModal) {
          setRegInterestData({
            'Property Type': values.propertyType,
            'Approximate Budget Range': values.budgetRange,
            'Are you approved for a mortgage?': values.areYouApproved,
            'Do you have a property to sell?': values.propertyToSell,
          });
        }
      }

      setLoading(true);
      const recaptchaKey = await getCaptchaKey('enquire');

      const { success, errors, leadId } = await asyncDispatch(
        newEnquiryRecaptcha(
          {
            propertyId: property.id,
            propertyType: property.type,
            contact: values.accountNumber,
            recaptcha: recaptchaKey,
            name: values.name.trim(),
            email: values.email.trim(),
            tel: `${values.prefix}${values.tel}`,
            comments: values.body.trim(),
            customData: registerInterestModal
              ? {
                  'Property Type': values.propertyType,
                  'Approximate Budget Range': values.budgetRange,
                  'Are you approved for a mortgage?': values.areYouApproved,
                  'Do you have a property to sell?': values.propertyToSell,
                }
              : {
                  "I'd like to request a viewing": values.requestViewing ? 'Yes' : 'No',
                },
          },
          IMAGE_LOCATOR,
        ),
      );

      if (success) {
        onSuccess(values.accountNumber, leadId);
        setSent(true);
      } else {
        if (errors) {
          helpers.setErrors(errors);
        } else {
          openToast({
            message: "Something went wrong. We couldn't send your enquiry. Please try again!",
            type: 'error',
            title: 'Error',
          });
        }

        setLoading(false);
      }
    },
  });

  const handleBodyFocus = () => {
    if (!registerInterestModal && formik.values.body === defaultText) {
      formik.setFieldValue('body', '');
    }
  };

  const handleBodyBlur = () => {
    if (!registerInterestModal && formik.values.body.trim() === '') {
      formik.setFieldValue('body', defaultText);
    }
  };

  useCachedUser(loggedInUser, (profile) => {
    formik.setValues({
      ...formik.values,
      name: profile.name,
      prefix: profile.prefix,
      tel: profile.phoneNumberNoPrefix,
      email: profile.email,
      rememberMe: true,
    });
  });

  if (sent)
    return (
      <Box
        data-testid="enquiry-sent"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        m="40px auto"
      >
        <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: 46 }} />
        <SubHeading textTransform="uppercase" mt={20} fontSize={28} fontWeight="600">
          Thanks!
        </SubHeading>
        <Text mt={20} align="center" fontSize={18}>
          Your enquiry has been sent successfully.
        </Text>
      </Box>
    );

  return (
    <>
      <Recaptcha />

      <form
        onSubmit={formik.handleSubmit}
        data-testid={`${registerInterestModal ? 'register-interest' : 'enquiry'}-form`}
      >
        <EnquiryFormContainer
          data-testid="enquiry-form-container"
          id={`${registerInterestModal ? 'register-interest' : 'enquiry'}-form`}
          col={!registerInterestModal}
        >
          <EnquiryFormColumn>
            {!registerInterestModal && contactOptions && contactOptions.length > 1 && (
              <EnquiryFormInput data-testid="accountNumber-enquiry">
                <SelectInput
                  id="accountNumber"
                  ariaLabel="Preferred Contact"
                  label="Preferred Contact"
                  options={contactOptions}
                  placeholder="Select a contact"
                  value={formik.values.accountNumber}
                  error={formik.touched.accountNumber && formik.errors.accountNumber}
                  onChange={formik.handleChange('accountNumber')}
                  testID="accountNumberSelect"
                />
              </EnquiryFormInput>
            )}

            <EnquiryFormInput>
              <TextInput
                name="name"
                placeholder="Your name"
                label="Name"
                value={formik.values.name}
                error={formik.touched.name && formik.errors.name}
                onValueChange={formik.handleChange('name')}
              />
            </EnquiryFormInput>

            <EnquiryFormInput>
              <TextInput
                name="email"
                placeholder="Your email address"
                label="Email"
                value={formik.values.email}
                error={formik.touched.email && formik.errors.email}
                onValueChange={formik.handleChange('email')}
              />
            </EnquiryFormInput>

            <EnquiryFormInput>
              <PhoneNumberInput
                name="phoneNumber"
                placeholder="Your telephone number"
                label="Phone"
                value={formik.values.tel}
                onValueChange={formik.handleChange('tel')}
                prefix={formik.values.prefix}
                onPrefixChange={formik.handleChange('prefix')}
                error={formik.touched.tel && (formik.errors.tel || formik.errors.prefix)}
              />
            </EnquiryFormInput>

            {registerInterestModal && (
              <>
                <EnquiryFormInput>
                  <SelectInput
                    testID="property-type-enquiry"
                    id="property-type-enquiry"
                    placeholder="Property type"
                    ariaLabel="Property type"
                    label="Property type"
                    options={propertyTypeOptions}
                    value={formik.values.propertyType}
                    error={formik.errors.propertyType && formik.errors.propertyType}
                    onChange={formik.handleChange('propertyType')}
                  />
                </EnquiryFormInput>

                <EnquiryFormInput>
                  <TextInput
                    testID="budget-range-enquiry"
                    name="budgetRange"
                    placeholder="Approximate budget range"
                    label="What is your approximate budget range?"
                    value={formik.values.budgetRange}
                    onValueChange={formik.handleChange('budgetRange')}
                  />
                </EnquiryFormInput>
              </>
            )}
          </EnquiryFormColumn>

          <EnquiryFormColumn>
            {registerInterestModal && (
              <>
                <EnquiryFormInput>
                  <SelectInput
                    testID="approved-enquiry"
                    id="approved-enquiry"
                    ariaLabel="Are you approved for a mortgage? (if required)"
                    label="Are you approved for a mortgage? (if required)"
                    options={approvedOptions}
                    placeholder="Are you approved for a mortgage?"
                    value={formik.values.areYouApproved}
                    error={formik.touched.areYouApproved && formik.errors.areYouApproved}
                    onChange={formik.handleChange('areYouApproved')}
                  />
                </EnquiryFormInput>

                <EnquiryFormInput>
                  <SelectInput
                    testID="to-sell-enquiry"
                    id="to-sell-enquiry"
                    ariaLabel="Do you have a property to sell?"
                    label="Do you have a property to sell?"
                    options={toSellOptions}
                    placeholder="Do you have a property to sell?"
                    value={formik.values.propertyToSell}
                    error={formik.touched.propertyToSell && formik.errors.propertyToSell}
                    onChange={formik.handleChange('propertyToSell')}
                  />
                </EnquiryFormInput>
              </>
            )}
            <EnquiryFormInput>
              <TextArea
                name="body"
                label={registerInterestModal ? 'Additional comments' : ''}
                value={formik.values.body}
                onValueChange={formik.handleChange('body')}
                placeholder={registerInterestModal ? regInterestBodyPlaceholder : 'Comments'}
                rows={registerInterestModal ? 4 : 6}
                testID="body-enquiry"
                error={formik.touched.body && formik.errors.body}
                onFocus={handleBodyFocus}
                onBlur={handleBodyBlur}
              />
            </EnquiryFormInput>

            {registerInterestModal && (
              <EnquiryFormInput>
                <SelectInput
                  testID="agents-enquiry"
                  id="agents-enquiry"
                  ariaLabel="Register your interest with the selling agent"
                  label="Register your interest with the selling agent(s)"
                  options={contactOptions}
                  placeholder="Select the Selling Agent you'd like to communicate with?"
                  value={formik.values.accountNumber}
                  onChange={formik.handleChange('accountNumber')}
                  error={formik.touched.accountNumber && formik.errors.accountNumber}
                />
              </EnquiryFormInput>
            )}

            {!loggedIn && (
              <EnquiryFormInput mt={20}>
                <Checkbox
                  name="rememberMe"
                  label="Remember my details"
                  value={formik.values.rememberMe}
                  onValueChange={(value) => formik.setFieldValue('rememberMe', value)}
                />
              </EnquiryFormInput>
            )}

            {!registerInterestModal && (
              <>
                <EnquiryFormInput mt={20}>
                  <Checkbox
                    name="requestViewing"
                    label="I'd like to request a viewing"
                    value={formik.values.requestViewing}
                    onValueChange={(value) => formik.setFieldValue('requestViewing', value)}
                  />
                </EnquiryFormInput>

                <EnquiryFormInput mt={20}>
                  <Button
                    color={pdpBrandingButtonBgColour}
                    fontColor={pdpBrandingButtonTextColour}
                    loading={loading}
                    testID="submit-enquiry"
                    loadingTestID="submitting-enquiry"
                    type="submit"
                    className={registerInterestModal ? 'pp-register-interest' : undefined}
                  >
                    Send Message
                  </Button>
                </EnquiryFormInput>
              </>
            )}
          </EnquiryFormColumn>
        </EnquiryFormContainer>

        {registerInterestModal && (
          <EnquiryFormButtonContainer>
            <Button
              loading={loading}
              testID="register-interest-enquiry"
              loadingTestID="registering-interest-enquiry"
              type="submit"
            >
              Register Your Interest
            </Button>
          </EnquiryFormButtonContainer>
        )}
      </form>
    </>
  );
};

export default EnquiryForm;
